import { graphql } from 'react-relay';
import React, { useContext } from 'react';
import { useEditorAnalyticsEvent } from '@confluence/editor-features/entry-points/useEditorAnalyticsEvent.ts';
import { useEditorBreadcrumbs } from '@confluence/editor-features/entry-points/useEditorBreadcrumbs.ts';
import { getNearestVisibleAncestor } from '@confluence/editor-features/entry-points/getNearestVisibleAncestor.ts';
import { ContentTypeType } from '@confluence/editor-features/entry-points/ContentTypeType.ts';
import { SessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy.ts';
import { useBlogTreeActions } from '@confluence/blog-tree-store/entry-points/useBlogTreeStore.ts';

import { EditorRevertDialogComponent } from './EditorRevertDialogComponent.tsx';
import type { EditorRevertDialogProps } from './editorRevertDialogTypes.ts';
import { useContentRelations } from './hooks/useContentRelations.ts';
import { useEditorRevertDialogContext } from './EditorRevertDialogContext.tsx';
import DeleteDraftMutationForRevertDialog from './__generated__/DeleteDraftMutationForRevertDialogMutation.graphql.ts';
import type { DeleteDraftMutationForRevertDialog as DeleteDraftMutationType } from './__types__/DeleteDraftMutationForRevertDialog.ts';
import { getBlueprintType } from './utilities.ts';
import type { EditorRevertDialogContentQuery as EditorRevertDialogContentQuery$data } from './__types__/EditorRevertDialogContentQuery.ts';
import { useExperimentalRevertContentMutation } from './useExperimentalRevertContentMutation.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const EditorRevertDialogContentQuery = graphql`
	query EditorRevertDialogContentQuery($contentId: ID, $status: [String]) {
		content(id: $contentId, status: $status) {
			nodes {
				id
				type
				version {
					number
				}
				metadata {
					labels {
						id
						name
					}
				}
				links {
					webui
					context
				}
			}
		}
	}
`;

export const EditorRevertDialog = (props: Omit<EditorRevertDialogProps, 'content'>) => {
	const { contentId, isUnpublishedDraft } = props;
	const { data, loading, error } = useQueryInterop<EditorRevertDialogContentQuery$data>(
		EditorRevertDialogContentQuery,
		{
			variables: {
				contentId,
				status: [isUnpublishedDraft ? 'draft' : 'current'],
			},
		},
	);

	if (loading || error) {
		return null;
	}

	const content = (data?.content?.nodes ?? [])[0] ?? null;

	return <EditorRevertDialogContainer {...props} content={content} />;
};

const EditorRevertDialogContainer = (props: EditorRevertDialogProps) => {
	const {
		relations: collaborators,
		loading: fetchingCollaboratorRelations,
		error: errorFetchingCollaborators,
	} = useContentRelations('collaborator', props.contentId, props.isUnpublishedDraft);
	const {
		relations: touched,
		loading: fetchingTouchedRelations,
		error: errorFetchingTouched,
	} = useContentRelations('touched', props.contentId, props.isUnpublishedDraft);
	const analyticsData = {
		blueprintType: getBlueprintType(props.content),
		contentId: props.contentId,
		contentType: props.content?.type ?? '',
		spaceKey: props.spaceKey,
		version: props.content?.version?.number ?? undefined,
		source: 'next-EditorRevertDialogComponent',
		subject: 'revertOrDeleteDialog',
	};
	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent(analyticsData);
	const { forceRefetchBlogTree } = useBlogTreeActions();

	const { matchSupportedRoute, push, replace, match } = useContext(RoutesContext);
	const { routePolicy } = useNavigationPolicy();
	const shouldPushInSelf = (path) => {
		return (
			!routePolicy ||
			routePolicy.willPushInSelf(match?.name, match?.params.contentId, path, undefined)
		);
	};

	const {
		shouldShowRevertDialog,
		shouldShowRevertDialogDiff,
		hideRevertDialogDiff,
		showRevertDialogDiff,
		hideRevertDialog,
	} = useEditorRevertDialogContext();
	const mutationVars = { contentId: props.contentId };

	const onCompleted = (data) => {
		if (data.experimentalRevertContent) {
			hideRevertDialog();
			const viewPagePath =
				(props.content?.links?.context ?? '') + (props.content?.links?.webui ?? '');
			props.cancelDebouncedSaveDraft?.();
			if (shouldPushInSelf(matchSupportedRoute(viewPagePath))) {
				if (viewPagePath !== '') {
					push(viewPagePath);
				}
			}
		}
	};

	const [revertContent] = useExperimentalRevertContentMutation({ onCompleted });
	const [deleteContent] = useMutationInterop<DeleteDraftMutationType>(
		DeleteDraftMutationForRevertDialog,
		{
			variables: mutationVars,
			onCompleted: (data) => {
				if (data.deleteContent.contentId) {
					const route = getAncestorRoute(props.spaceKey);
					hideRevertDialog();
					props.cancelDebouncedSaveDraft?.();
					if (shouldPushInSelf(route)) {
						replace(route);
					}

					if (props.content?.type === ContentTypeType.BLOGPOST) {
						// update blog tree after unpublished blog draft is deleted
						forceRefetchBlogTree();
					}
				}
			},
		},
	);

	const errorFetchingRelations =
		errorFetchingCollaborators !== undefined || errorFetchingTouched !== undefined;

	const breadcrumbs = useEditorBreadcrumbs(props.contentId);
	const getAncestorRoute = (spaceKey) => {
		// Gets the nearest ancestor in breadcrumbs. Use space index as fallback route in case of error or if page is a blog
		let url = SPACE_OVERVIEW.toUrl({ spaceKey });
		if (props.content?.type === ContentTypeType.PAGE && breadcrumbs) {
			url = getNearestVisibleAncestor({
				breadcrumbs,
				fallbackUrl: url,
			});
		}
		return url;
	};

	return (
		<SessionData>
			{(sessionData) => (
				<EditorRevertDialogComponent
					content={props.content}
					contentId={props.contentId}
					isUnpublishedDraft={props.isUnpublishedDraft}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					collaboratorRelations={collaborators}
					touchedRelations={touched}
					fetchingCollaboratorRelations={fetchingCollaboratorRelations}
					fetchingTouchedRelations={fetchingTouchedRelations}
					shouldShowRevertDialog={shouldShowRevertDialog}
					shouldShowRevertDialogDiff={shouldShowRevertDialogDiff}
					hideRevertDialogDiff={hideRevertDialogDiff}
					showRevertDialogDiff={showRevertDialogDiff}
					hideRevertDialog={hideRevertDialog}
					errorFetchingRelations={errorFetchingRelations}
					revertContent={revertContent}
					deleteContent={deleteContent}
					currentAccountId={sessionData.userId}
					contentType={props.content?.type as ContentTypeType}
				/>
			)}
		</SessionData>
	);
};
